import { alpha, Box, Theme, Typography } from "@mui/material";
import moment from "moment";
import { DragEvent, MouseEvent } from "react";
import { Task } from "../../../api/Tasks";
import { colorOrNeutral, projectColorText } from "../../../misc/projectColors";
import { TaskCheckbox } from "../TaskCheckbox";
import { shortDate } from "../../../misc/dates";
import { ProjectCode } from "../../project/ProjectCode";
import { CopyLinkButton } from "../CopyLinkButton";

export function CalendarTask(props: {
    task: Task;
    selected: boolean;
    disabled?: boolean;
    onClick: (e: MouseEvent) => void;
    onContextMenu: (e: MouseEvent) => void;
    onToggle: (checked: boolean) => void;

    // showTeam names the task's team, for the "All" workspace
    showTeam?: boolean;

    // showCopy puts a button that copies the task's permalink at the end, shown on hover
    showCopy?: boolean;

    draggable?: boolean;
    onDragStart?: (e: DragEvent) => void;
    onDragEnd?: () => void;
    onDragOver?: (e: DragEvent) => void;
    onDrop?: (e: DragEvent) => void;
}) {
    const task = props.task;
    const done = !!task.completedAt;

    // a task working towards a project wears that project's colour, so the calendar
    // shows at a glance what a day is made up of. One filed under nothing wears the
    // neutral tint rather than a colour it doesn't have.
    const color = colorOrNeutral(task.projectColor);

    // a task that's been dealt with drops the colour and goes plain grey, so the day
    // reads as what it still has left in it
    const bg = done
        ? { rest: "grey.100", hover: "grey.200" }
        : { rest: color.bg, hover: color.hover };

    // what the task is due on and what it hangs off sit under the title rather than
    // crowding in front of it, and the line goes altogether when there's none of it
    const carriedDate = task.carriedOver && task.dueDate ? shortDate(task.dueDate) : "";
    const parent = task.type === "checklist" ? task.parentTitle : "";
    const team = props.showTeam ? task.teamName : "";
    const hasMeta = !!carriedDate || !!task.dueTime || !!parent || !!team;

    return (
        <Box
            onClick={e => {
                e.stopPropagation();
                props.onClick(e);
            }}
            // a shift-click selects a range of tasks rather than the text under it
            onMouseDown={e => {
                if (e.shiftKey) e.preventDefault();
            }}
            onContextMenu={props.onContextMenu}
            // the hover text gives the whole of a clipped title, and names what the
            // chip does besides open
            title={task.title + "\n" + (props.draggable ? "Drag to move \u00b7 " : "") + "Right-click for more"}
            draggable={props.draggable}
            onDragStart={props.onDragStart}
            onDragEnd={props.onDragEnd}
            onDragOver={props.onDragOver}
            onDrop={props.onDrop}
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.25,
                minWidth: 0,
                // a busy day scrolls rather than squashing its items
                flexShrink: 0,
                pr: 0.5,
                borderRadius: 0.75,
                // a hairline all the way round holds the chip together on a tinted day,
                // with the accent edge on the left
                border: 1,
                borderColor: "rgba(0, 0, 0, 0.12)",
                borderLeft: 3,
                borderLeftColor: done ? "success.main" : color.border,
                bgcolor: bg.rest,
                color: done ? "text.secondary" : projectColorText(),
                cursor: "pointer",
                transition: theme => theme.transitions.create(["background-color", "box-shadow"], {
                    duration: theme.transitions.duration.shortest,
                }),
                "&:hover": {
                    bgcolor: bg.hover,
                    boxShadow: 1,
                },
                "&:hover .calendar-task-copy": { opacity: 1 },
                // a task carried over from a day that has passed is ringed so it reads
                // as a visitor to the day it's sitting in. An outline rather than a
                // border so it doesn't fight the project colour on the left edge.
                ...(task.carriedOver && {
                    outline: "1px dashed",
                    outlineColor: "warning.main",
                    outlineOffset: "-1px",
                }),
                // a selected task needs to stand out from the rest of the day at a
                // glance, so it takes a heavy ring and a tint of its own. The ring sits
                // inside the border, so the project colour on the left edge stays.
                ...(props.selected && {
                    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.16),
                    borderColor: "primary.main",
                    borderLeftColor: done ? "success.main" : color.border,
                    boxShadow: (theme: Theme) => "inset 0 0 0 2px " + theme.palette.primary.main,
                    outline: "none",
                    "&:hover": {
                        bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.22),
                    },
                }),
            }}
        >
            <Box onClick={e => e.stopPropagation()} sx={{ flexShrink: 0, display: "flex" }}>
                <TaskCheckbox
                    dense
                    checked={done}
                    disabled={props.disabled}
                    // the empty circle wears a washed-out version of the task's own
                    // accent, at the weight grey.400 carries on an untinted card
                    tint={alpha(color.border, 0.35)}
                    onChange={props.onToggle}
                />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, py: 0.125 }}>
                {/* a title that doesn't fit runs onto a second line and is cut off after
                    that, so a long task is readable without a day growing without limit */}
                <Typography variant="caption" component="div" sx={{
                    lineHeight: 1.4,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    // a long unbroken word breaks rather than widening the chip
                    overflowWrap: "anywhere",
                    color: done ? "text.secondary" : projectColorText(),
                }}>
                    {task.title}
                </Typography>

                {hasMeta && (
                    <Typography variant="caption" component="div" noWrap sx={{
                        lineHeight: 1.3,
                        fontSize: 11,
                        color: "text.secondary",
                    }}>
                        {carriedDate && (
                            <Box component="span" sx={{ color: "warning.dark", mr: 0.5 }}>
                                {carriedDate}
                            </Box>
                        )}
                        {task.dueTime && (
                            <Box component="span" sx={{ mr: 0.5 }}>
                                {moment(task.dueTime, "HH:mm").format("h:mma")}
                            </Box>
                        )}
                        {[parent, team].filter(v => !!v).join(" · ")}
                    </Typography>
                )}
            </Box>

            <Box sx={{ flexShrink: 0, py: 0.125, fontSize: 11 }}>
                <ProjectCode code={task.projectCode} color={task.projectColor} done={done} />
            </Box>

            {props.showCopy && (
                <Box sx={{ mt: -0.25 }}>
                    <CopyLinkButton task={task} className="calendar-task-copy" opacity={0} />
                </Box>
            )}
        </Box>
    );
}
